/**
 * =============================================================================
 * ENTERPRISE JOB WORKSPACE - Operational Command Console
 * =============================================================================
 * High-priority operational screen for helpdesk, operations, finance, managers.
 * Designed for all-day use with instant situational awareness.
 * 
 * Layout:
 * - TOP HEADER: Job ref, site, resident, status, SLA countdown, priority, contractor
 * - LEFT COLUMN: Overview, Communications, Evidence, Timeline
 * - CENTRE COLUMN: Workflow progress, Quote/Scheduling/Completion workflows
 * - RIGHT COLUMN (sticky): Take Action panel, Finance, Quick Contact
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, RefreshCw, Edit, Plus, MoreHorizontal, 
  Building2, Wrench, User, Phone, Mail, MessageSquare,
  Clock, Flag, FileText, Image, Award, CheckCircle, Check,
  MapPin, ExternalLink, Send, Paperclip, ChevronDown, ChevronUp,
  AlertTriangle, Eye, Ban, Calendar, Copy, X, Loader2,
  UserPlus, Search, DollarSign, Receipt, Camera, Upload,
  AlertCircle, TrendingUp, Link2, History, Shield, Zap,
  Pause, Play, FileCheck
} from 'lucide-react';
import { TOKENS } from './PHSCommandLayout';
import { 
  SLAStatusStrip, 
  QuoteReviewDrawer, 
  InvoiceReviewDrawer, 
  EnhancedEvidencePanel, 
  UnifiedActivityFeed 
} from './JobWorkspaceDrawers';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// CONSTANTS - Synchronized with workspace/constants.js for granular statuses
// =============================================================================
const STATUS_CONFIG = {
  // Primary Workflow Statuses
  'Job Created': { color: '#0D9488', bg: '#CCFBF1', label: 'New Enquiry' },
  'Assigned': { color: '#3B82F6', bg: '#DBEAFE', label: 'Assigned' },
  
  // Quote Workflow
  'Awaiting Quote': { color: '#F97316', bg: '#FED7AA', label: 'Awaiting Quote' },
  'Quote Awaiting Review': { color: '#EAB308', bg: '#FEF9C3', label: 'Quote Review' },
  'Quote Queried': { color: '#DC2626', bg: '#FEE2E2', label: 'Quote Queried' },
  'Quote Sent For Approval': { color: '#A855F7', bg: '#F3E8FF', label: 'Sent For Approval' },
  'Quote Awaiting Approval': { color: '#A855F7', bg: '#F3E8FF', label: 'Quote Approval' },
  'Quote Approved': { color: '#22C55E', bg: '#DCFCE7', label: 'Quote Approved' },
  'Quote Rejected': { color: '#DC2626', bg: '#FEE2E2', label: 'Quote Rejected' },
  
  // PO & Scheduling
  'Approved': { color: '#22C55E', bg: '#DCFCE7', label: 'Approved' },
  'PO Generated': { color: '#22C55E', bg: '#DCFCE7', label: 'PO Generated' },
  'PO Issued': { color: '#22C55E', bg: '#DCFCE7', label: 'PO Issued' },
  'Scheduled': { color: '#6366F1', bg: '#E0E7FF', label: 'Scheduled' },
  'In Progress': { color: '#3B82F6', bg: '#DBEAFE', label: 'In Progress' },
  
  // Completion Workflow
  'Awaiting Evidence': { color: '#F97316', bg: '#FED7AA', label: 'Awaiting Evidence' },
  'SAT Note Missing': { color: '#DC2626', bg: '#FEE2E2', label: 'SAT Note Missing' },
  'Completed': { color: '#22C55E', bg: '#DCFCE7', label: 'Completed' },
  
  // Invoice Workflow
  'Awaiting Invoice': { color: '#F97316', bg: '#FED7AA', label: 'Awaiting Invoice' },
  'Invoice Submitted': { color: '#6366F1', bg: '#E0E7FF', label: 'Invoice Submitted' },
  'Invoice Queried': { color: '#DC2626', bg: '#FEE2E2', label: 'Invoice Queried' },
  'Invoice Approved': { color: '#22C55E', bg: '#DCFCE7', label: 'Invoice Approved' },
  'Invoiced': { color: '#6366F1', bg: '#E0E7FF', label: 'Invoiced' },
  'Ready For Payment': { color: '#22C55E', bg: '#DCFCE7', label: 'Ready For Payment' },
  'Paid': { color: '#22C55E', bg: '#DCFCE7', label: 'Paid' },
  
  // Terminal States
  'Closed': { color: '#6B7280', bg: '#F3F4F6', label: 'Closed' },
  'Cancelled': { color: '#DC2626', bg: '#FEE2E2', label: 'Cancelled' },
  
  // Legacy status mappings for backward compatibility
  'New': { color: '#0D9488', bg: '#CCFBF1', label: 'New Enquiry' },
  'Quote Received': { color: '#EAB308', bg: '#FEF9C3', label: 'Quote Received' },
  'Quote Requested': { color: '#F97316', bg: '#FED7AA', label: 'Quote Requested' },
  'Pending': { color: '#0D9488', bg: '#CCFBF1', label: 'Pending' },
};

const PRIORITY_CONFIG = {
  emergency: { color: '#DC2626', bg: '#FEE2E2', label: 'Emergency' },
  high: { color: '#F59E0B', bg: '#FEF3C7', label: 'High' },
  medium: { color: '#3B82F6', bg: '#DBEAFE', label: 'Medium' },
  low: { color: '#6B7280', bg: '#F3F4F6', label: 'Low' },
};

const WORKFLOW_STAGES = [
  { key: 'enquiry', label: 'Enquiry', statuses: ['Job Created', 'New', 'Pending'] },
  { key: 'assigned', label: 'Assigned', statuses: ['Assigned'] },
  { key: 'quote', label: 'Quote', statuses: ['Awaiting Quote', 'Quote Awaiting Review', 'Quote Queried', 'Quote Sent For Approval', 'Quote Awaiting Approval', 'Quote Approved', 'Quote Rejected', 'Quote Requested', 'Quote Received'] },
  { key: 'approval', label: 'Approval', statuses: ['Approved', 'PO Generated', 'PO Issued'] },
  { key: 'scheduled', label: 'Scheduled', statuses: ['Scheduled'] },
  { key: 'progress', label: 'In Progress', statuses: ['In Progress', 'Awaiting Evidence', 'SAT Note Missing'] },
  { key: 'completed', label: 'Completed', statuses: ['Completed', 'Awaiting Invoice', 'Invoice Submitted', 'Invoice Queried', 'Invoice Approved', 'Invoiced', 'Ready For Payment', 'Paid', 'Closed'] },
];

// =============================================================================
// MODAL COMPONENT
// =============================================================================
function Modal({ isOpen, onClose, title, children, size = 'md' }) {
  if (!isOpen) return null;
  
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-2xl ${sizeClasses[size]} w-full`}>
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// JOB HEADER - Critical operational info at a glance
// =============================================================================
function JobHeader({ job, onBack, onAction, loading, showMoreMenu, setShowMoreMenu }) {
  const status = STATUS_CONFIG[job.status] || STATUS_CONFIG['Job Created'];
  const priority = PRIORITY_CONFIG[job.priority?.toLowerCase()] || PRIORITY_CONFIG.medium;
  const jobRef = job.job_reference || `JOB-${job.id?.slice(0, 8).toUpperCase()}`;
  
  // SLA Calculation
  const getSLAInfo = () => {
    if (!job.sla_due_at) return { text: 'No SLA', color: '#6B7280', urgent: false, severity: 'none', percentage: 0 };
    const due = new Date(job.sla_due_at);
    const now = new Date();
    const created = job.created_at ? new Date(job.created_at) : new Date(now.getTime() - 86400000 * 7); // Default 7 days ago
    const diffMs = due - now;
    const totalMs = due - created;
    const elapsedMs = now - created;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Calculate percentage of SLA elapsed (capped at 100)
    const percentage = Math.min(100, Math.max(0, Math.round((elapsedMs / totalMs) * 100)));
    
    if (diffMs < 0) {
      const overdueDays = Math.abs(diffDays);
      return { text: `${overdueDays}d overdue`, color: '#DC2626', urgent: true, breached: true, severity: 'critical', percentage: 100 };
    } else if (diffHours < 24) {
      return { text: `${diffHours}h remaining`, color: '#F59E0B', urgent: true, severity: 'warning', percentage };
    } else if (diffDays <= 2) {
      return { text: `${diffDays}d remaining`, color: '#F59E0B', urgent: false, severity: 'warning', percentage };
    }
    return { text: `${diffDays}d remaining`, color: '#22C55E', urgent: false, severity: 'ok', percentage };
  };
  const sla = getSLAInfo();
  
  // Escalation check
  const isEscalated = job.escalated || job.priority?.toLowerCase() === 'emergency' || sla.breached;
  
  // Get primary next action
  const getNextAction = () => {
    const s = job.status?.toLowerCase()?.replace(/\s+/g, '_');
    const actions = {
      job_created: { label: 'Assign Contractor', action: 'assign', color: '#0D9488' },
      assigned: { label: 'Chase for Quote', action: 'chase', color: '#3B82F6' },
      awaiting_quote: { label: 'Chase Contractor', action: 'chase', color: '#F97316' },
      quote_awaiting_review: { label: 'Review Quote', action: 'review_quote', color: '#EAB308' },
      quote_awaiting_approval: { label: 'Approve Quote', action: 'approve', color: '#A855F7' },
      approved: { label: 'Schedule Work', action: 'schedule', color: '#22C55E' },
      po_issued: { label: 'Schedule Work', action: 'schedule', color: '#22C55E' },
      scheduled: { label: 'Confirm Start', action: 'confirm_start', color: '#6366F1' },
      in_progress: { label: 'Mark Complete', action: 'mark_complete', color: '#3B82F6' },
      completed: { label: 'Request Invoice', action: 'request_invoice', color: '#22C55E' },
      awaiting_invoice: { label: 'Review Invoice', action: 'review_invoice', color: '#F97316' },
      invoiced: { label: 'Approve Payment', action: 'approve_invoice', color: '#6366F1' },
    };
    return actions[s] || { label: 'View Details', action: 'view', color: '#6B7280' };
  };
  const nextAction = getNextAction();

  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-10" data-testid="job-header">
      {/* Top Bar */}
      <div className="px-4 lg:px-6 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Job Pipeline</span>
        </button>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Job ID:</span>
          <span className="font-bold text-slate-800">{jobRef}</span>
          <button className="p-1 hover:bg-slate-200 rounded" onClick={() => navigator.clipboard.writeText(jobRef)}>
            <Copy className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => onAction('edit')} 
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
            title="Edit Job"
            data-testid="edit-job-btn"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onAction('note')} 
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
            title="Add Note"
            data-testid="add-note-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
              title="More Actions"
              data-testid="more-actions-btn"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMoreMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50">
                <button
                  onClick={() => { onAction('schedule'); setShowMoreMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 rounded-t-xl"
                >
                  Schedule/Reschedule
                </button>
                <button
                  onClick={() => { onAction('assign'); setShowMoreMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                >
                  Assign Contractor
                </button>
                <button
                  onClick={() => { onAction('escalate'); setShowMoreMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 text-amber-600"
                >
                  Escalate
                </button>
                <button
                  onClick={() => { onAction('cancel'); setShowMoreMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 text-red-600 rounded-b-xl"
                >
                  Cancel Job
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Header Content */}
      <div className="px-4 lg:px-6 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Left - Job ID & Status */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-800">{jobRef}</h1>
              <span 
                className="px-3 py-1 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: status.bg, color: status.color }}
              >
                {status.label}
              </span>
              <span 
                className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1"
                style={{ backgroundColor: priority.bg, color: priority.color }}
              >
                <Flag className="w-3 h-3" />
                {priority.label}
              </span>
              {isEscalated && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Escalated
                </span>
              )}
            </div>
            
            {/* Meta Info Row */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              {/* Location - Clickable to open park info */}
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-slate-400" />
                <button 
                  onClick={() => window.location.href = `/phs/assets?tab=parks&search=${encodeURIComponent(job.park_name || job.site_name || '')}`}
                  className="font-medium hover:text-teal-600 hover:underline"
                  title="View Park Details"
                >
                  {job.park_name || job.site_name || 'Unknown Park'}
                </button>
                {job.plot_number && <span className="text-slate-400">Plot {job.plot_number}</span>}
                {/* Google Maps Link */}
                {(job.park_postcode || job.postcode) && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((job.park_name || job.site_name || '') + ' ' + (job.park_postcode || job.postcode || ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-teal-600"
                    title="Open in Google Maps"
                  >
                    <MapPin className="w-4 h-4" />
                  </a>
                )}
              </div>
              {/* Category/Scope - Clickable to edit */}
              <button 
                onClick={() => onAction('edit')}
                className="flex items-center gap-1.5 hover:text-teal-600 group"
                title="Edit Scope"
              >
                <Wrench className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                <span>{job.category || 'General'}</span>
                <Edit className="w-3 h-3 opacity-0 group-hover:opacity-100 text-teal-600" />
              </button>
              {/* Resident - Add/Edit */}
              <button 
                onClick={() => onAction('edit_resident')}
                className="flex items-center gap-1.5 hover:text-teal-600 group"
                title={job.resident_name || job.client_name ? 'Edit Resident Info' : 'Add Resident Info'}
              >
                <User className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                <span className={!job.resident_name && !job.client_name ? 'text-amber-600' : ''}>
                  {job.resident_name || job.client_name || 'Add Resident'}
                </span>
                {(!job.resident_name && !job.client_name) && (
                  <Plus className="w-3 h-3 text-amber-600" />
                )}
              </button>
            </div>
          </div>

          {/* Right - SLA & Primary CTA */}
          <div className="flex items-center gap-4">
            {/* SLA Badge */}
            <div 
              className="px-4 py-3 rounded-xl text-center min-w-[140px]"
              style={{ backgroundColor: `${sla.color}15`, borderLeft: `4px solid ${sla.color}` }}
            >
              <div className="flex items-center gap-2 justify-center mb-1">
                <Clock className="w-4 h-4" style={{ color: sla.color }} />
                <span className="text-xs font-medium text-slate-600">SLA Due</span>
              </div>
              <div className="text-sm font-bold" style={{ color: sla.color }}>
                {job.sla_due_at ? new Date(job.sla_due_at).toLocaleDateString() : 'Not Set'}
              </div>
              <div className="text-xs font-semibold" style={{ color: sla.color }}>
                {sla.text}
              </div>
            </div>

            {/* Primary CTA */}
            <button
              onClick={() => onAction(nextAction.action)}
              disabled={loading}
              className="px-6 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition flex items-center gap-2"
              style={{ backgroundColor: nextAction.color }}
              data-testid="primary-action-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {nextAction.label}
            </button>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* STAKEHOLDER STRIP - Horizontal cards layout */}
      {/* Park | Trade | Resident | Contractor | SLA */}
      {/* ============================================= */}
      <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 bg-white border-t border-slate-200">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap items-stretch gap-2 sm:gap-3">
          {/* Park Card */}
          <div 
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition"
            onClick={() => window.location.href = `/phs/assets?tab=parks&search=${encodeURIComponent(job.park_name || job.site_name || '')}`}
            data-testid="stakeholder-park"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-800 truncate text-sm sm:text-base">{job.park_name || job.site_name || 'Unknown Park'}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">{job.plot_number ? `Plot ${job.plot_number}` : 'Park'}</p>
            </div>
          </div>

          {/* Trade/Category Card */}
          <div 
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition"
            onClick={() => onAction('edit')}
            data-testid="stakeholder-trade"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-800 truncate text-sm sm:text-base">{job.category || 'General'}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">{job.sub_category || job.job_type || 'Trade'}</p>
            </div>
          </div>

          {/* Resident Card */}
          <div 
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition"
            onClick={() => onAction('edit_resident')}
            data-testid="stakeholder-resident"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${job.resident_name || job.client_name ? 'bg-green-100' : 'bg-amber-100'}`}>
              <User className={`w-4 h-4 sm:w-5 sm:h-5 ${job.resident_name || job.client_name ? 'text-green-600' : 'text-amber-600'}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`font-semibold truncate text-sm sm:text-base ${job.resident_name || job.client_name ? 'text-slate-800' : 'text-amber-600'}`}>
                {job.resident_name || job.client_name || 'Add Resident'}
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500">Resident</p>
            </div>
          </div>

          {/* Contractor Card */}
          <div 
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border rounded-lg cursor-pointer transition ${
              job.contractor_name || job.contractor_id 
                ? 'bg-teal-50 border-teal-200 hover:bg-teal-100' 
                : 'bg-amber-50 border-amber-200 border-dashed hover:bg-amber-100'
            }`}
            onClick={() => job.contractor_name || job.contractor_id ? onAction('view_contractor') : onAction('assign')}
            data-testid="stakeholder-contractor"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
              job.contractor_name || job.contractor_id ? 'bg-teal-600' : 'bg-amber-400'
            }`}>
              {job.contractor_name || job.contractor_id 
                ? (job.contractor_name || job.contractor?.company_name || 'C').charAt(0).toUpperCase()
                : <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
              }
            </div>
            <div className="min-w-0 flex-1">
              <p className={`font-semibold truncate text-sm sm:text-base ${job.contractor_name || job.contractor_id ? 'text-slate-800' : 'text-amber-700'}`}>
                {job.contractor?.company_name || job.contractor_name || 'Assign Contractor'}
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500 truncate">
                {job.contractor?.contact_name || job.contractor?.primary_trade || (job.contractor_name ? 'Contractor' : 'Click to assign')}
              </p>
            </div>
          </div>

          {/* SLA Card with circular progress - Full width on mobile, auto on desktop */}
          <div 
            className={`col-span-2 sm:col-span-1 lg:ml-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border rounded-lg ${
              sla.severity === 'critical' ? 'bg-red-50 border-red-200' :
              sla.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
              'bg-green-50 border-green-200'
            }`}
            data-testid="stakeholder-sla"
          >
            {/* Circular SLA Progress */}
            <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 transform -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle 
                  cx="18" cy="18" r="15.5" fill="none" 
                  stroke={sla.color} 
                  strokeWidth="3"
                  strokeDasharray={`${(sla.percentage || 0)} 100`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-bold" style={{ color: sla.color }}>
                {sla.percentage || 0}%
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] sm:text-xs font-medium text-slate-500">SLA Due</p>
              <p className="font-semibold text-xs sm:text-sm" style={{ color: sla.color }}>
                {job.sla_due_at ? new Date(job.sla_due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Not Set'}
              </p>
              <p className="text-[10px] sm:text-xs font-medium" style={{ color: sla.color }}>{sla.text}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Scope Summary Strip */}
      <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 bg-slate-50 border-t border-slate-100">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Scope Summary</span>
            <p className="text-sm text-slate-700 mt-1 line-clamp-2">{job.description || 'No description provided'}</p>
          </div>
          <button 
            onClick={() => onAction('edit')}
            className="text-xs text-teal-600 hover:text-teal-700 whitespace-nowrap flex items-center gap-1"
          >
            <Eye className="w-3 h-3" />
            View Full Scope
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// WORKFLOW PROGRESS TRACKER
// =============================================================================
function WorkflowProgressTracker({ job, compact = false }) {
  const currentStage = useMemo(() => {
    const status = job.status || 'Job Created';
    const idx = WORKFLOW_STAGES.findIndex(s => s.statuses.includes(status));
    return idx >= 0 ? idx : 0;
  }, [job.status]);

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {WORKFLOW_STAGES.map((stage, idx) => {
          const isComplete = idx < currentStage;
          const isCurrent = idx === currentStage;
          return (
            <div
              key={stage.key}
              className={`w-3 h-3 rounded-full ${
                isComplete ? 'bg-teal-500' :
                isCurrent ? 'bg-teal-500 ring-2 ring-teal-200' :
                'bg-slate-200'
              }`}
              title={stage.label}
            />
          );
        })}
        <span className="ml-2 text-xs text-slate-500">
          {currentStage + 1}/{WORKFLOW_STAGES.length}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="workflow-tracker">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Job Progress</h3>
        <span className="text-xs text-slate-500">
          Stage {currentStage + 1} of {WORKFLOW_STAGES.length}
        </span>
      </div>
      
      <div className="flex items-center justify-between relative">
        {/* Progress Line */}
        <div className="absolute left-4 right-4 top-4 h-0.5 bg-slate-200">
          <div 
            className="h-full bg-teal-500 transition-all"
            style={{ width: `${(currentStage / (WORKFLOW_STAGES.length - 1)) * 100}%` }}
          />
        </div>
        
        {/* Steps */}
        {WORKFLOW_STAGES.map((stage, idx) => {
          const isComplete = idx < currentStage;
          const isCurrent = idx === currentStage;
          
          return (
            <div key={stage.key} className="relative flex flex-col items-center z-10">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  isComplete ? 'bg-teal-500 text-white' :
                  isCurrent ? 'bg-teal-500 text-white ring-4 ring-teal-100' :
                  'bg-slate-200 text-slate-500'
                }`}
              >
                {isComplete ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <span className={`text-[10px] mt-2 text-center max-w-[60px] leading-tight ${
                isCurrent ? 'font-semibold text-teal-600' : 'text-slate-500'
              }`}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// SLA SUMMARY DONUT
// =============================================================================
function SLASummaryDonut({ job }) {
  const getSLAInfo = () => {
    if (!job.sla_due_at) return { elapsed: 0, remaining: 100, status: 'No SLA', color: '#6B7280' };
    
    const due = new Date(job.sla_due_at);
    const created = new Date(job.created_at || Date.now());
    const now = new Date();
    
    const totalMs = due - created;
    const elapsedMs = now - created;
    const elapsed = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
    
    if (now > due) {
      return { elapsed: 100, remaining: 0, status: 'Breached', color: '#DC2626' };
    } else if (elapsed > 75) {
      return { elapsed, remaining: 100 - elapsed, status: 'At Risk', color: '#F59E0B' };
    }
    return { elapsed, remaining: 100 - elapsed, status: 'On Track', color: '#22C55E' };
  };
  
  const sla = getSLAInfo();
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (sla.elapsed / 100) * circumference;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sla-summary">
      <h3 className="font-semibold text-slate-800 mb-3">SLA Summary</h3>
      
      <div className="flex items-center gap-4">
        {/* Donut */}
        <div className="relative w-24 h-24">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#E5E7EB" strokeWidth="8" />
            <circle 
              cx="50" cy="50" r="40" fill="none" 
              stroke={sla.color} strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-slate-800">{Math.round(sla.elapsed)}%</span>
            <span className="text-[10px] text-slate-500">Elapsed</span>
          </div>
        </div>
        
        {/* Info */}
        <div className="flex-1 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">SLA Target</span>
            <span className="font-medium text-slate-700">
              {job.sla_due_at ? new Date(job.sla_due_at).toLocaleString() : '-'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Time Remaining</span>
            <span className="font-medium" style={{ color: sla.color }}>
              {sla.remaining > 0 ? `${Math.round(sla.remaining)}%` : 'Overdue'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Status</span>
            <span 
              className="px-2 py-0.5 rounded text-xs font-semibold"
              style={{ backgroundColor: `${sla.color}20`, color: sla.color }}
            >
              {sla.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// FINANCIAL SNAPSHOT
// =============================================================================
function FinancialSnapshot({ job }) {
  const quote = job.quotes?.[0];
  const invoice = job.invoice;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="financial-snapshot">
      <h3 className="font-semibold text-slate-800 mb-3">Financial Snapshot</h3>
      
      <div className="space-y-3 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-slate-500">Quote Value</span>
          <span className="font-medium text-slate-700">
            {quote?.total_inc_vat ? `£${quote.total_inc_vat.toLocaleString()}` : '-'}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-500">Approved Value</span>
          <span className="font-medium text-slate-700">
            {job.approved_value ? `£${job.approved_value.toLocaleString()}` : '-'}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-500">Invoice Value</span>
          <span className="font-medium text-slate-700">
            {invoice?.total ? `£${invoice.total.toLocaleString()}` : '-'}
          </span>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
          <span className="text-slate-500">Paid</span>
          <span className={`font-semibold ${job.paid ? 'text-green-600' : 'text-slate-400'}`}>
            {job.paid ? `£${job.paid_amount?.toLocaleString() || '0'}` : 'No'}
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMMUNICATIONS PANEL
// =============================================================================
function CommunicationsPanel({ job, communications, onAddNote }) {
  const [filter, setFilter] = useState('all');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('internal');
  const [sending, setSending] = useState(false);
  
  const filters = [
    { key: 'all', label: 'All' },
    { key: 'internal', label: 'Internal Notes' },
    { key: 'contractor', label: 'Contractor' },
    { key: 'resident', label: 'Resident' },
    { key: 'system', label: 'System' },
  ];
  
  const filteredComms = useMemo(() => {
    if (filter === 'all') return communications;
    return communications.filter(c => c.type === filter || c.channel === filter);
  }, [communications, filter]);
  
  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await onAddNote({ message, type: messageType });
      setMessage('');
    } finally {
      setSending(false);
    }
  };
  
  const getCommIcon = (type) => {
    switch (type) {
      case 'internal': return <FileText className="w-3 h-3" />;
      case 'contractor': return <Wrench className="w-3 h-3" />;
      case 'resident': return <User className="w-3 h-3" />;
      case 'whatsapp': return <MessageSquare className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };
  
  const getCommColor = (type) => {
    switch (type) {
      case 'internal': return '#6366F1';
      case 'contractor': return '#F97316';
      case 'resident': return '#22C55E';
      case 'whatsapp': return '#25D366';
      default: return '#6B7280';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="communications-panel">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Communications</h3>
          <span className="text-xs text-slate-500">{communications.length} messages</span>
        </div>
        
        {/* Filters */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition ${
                filter === f.key 
                  ? 'bg-teal-50 text-teal-700 font-medium' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Message Input */}
      <div className="p-3 border-b border-slate-100 bg-slate-50">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a note or message..."
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none"
            data-testid="comms-input"
          />
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            className="px-2 py-2 text-xs border border-slate-200 rounded-lg bg-white"
          >
            <option value="internal">Internal</option>
            <option value="contractor">To Contractor</option>
            <option value="resident">To Resident</option>
          </select>
          <button className="p-2 text-slate-400 hover:text-slate-600">
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            onClick={handleSend}
            disabled={!message.trim() || sending}
            className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
        {filteredComms.map((comm, idx) => (
          <div key={idx} className="p-4 hover:bg-slate-50 transition">
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${getCommColor(comm.type)}20`, color: getCommColor(comm.type) }}
              >
                {getCommIcon(comm.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: getCommColor(comm.type) }}
                    >
                      {comm.type === 'internal' ? 'Internal Note' : 
                       comm.type === 'contractor' ? 'Contractor Message' :
                       comm.type === 'resident' ? 'Resident Message' :
                       comm.type === 'whatsapp' ? 'WhatsApp' : 'System Update'}
                    </span>
                    <span className="text-xs text-slate-500">{comm.author || 'System'}</span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {comm.created_at ? new Date(comm.created_at).toLocaleString() : ''}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{comm.message || comm.content || comm.note}</p>
              </div>
            </div>
          </div>
        ))}
        
        {filteredComms.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">
            No messages in this category
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// EVIDENCE PANEL
// =============================================================================
function EvidencePanel({ job }) {
  const evidence = job.evidence || {};
  const photos = evidence.photos || [];
  const documents = evidence.documents || [];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="evidence-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Evidence & Files</h3>
        <button className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1">
          <Upload className="w-3 h-3" />
          Upload
        </button>
      </div>
      
      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-2">Photos ({photos.length})</p>
          <div className="grid grid-cols-4 gap-2">
            {photos.slice(0, 4).map((photo, idx) => (
              <div key={idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                <img src={photo.url} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Documents */}
      {documents.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Documents ({documents.length})</p>
          <div className="space-y-2">
            {documents.slice(0, 3).map((doc, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-700 truncate flex-1">{doc.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {photos.length === 0 && documents.length === 0 && (
        <div className="text-center py-6">
          <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No evidence uploaded</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TIMELINE PANEL
// =============================================================================
function TimelinePanel({ job }) {
  const history = job.history || [];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="timeline-panel">
      <h3 className="font-semibold text-slate-800 mb-4">Activity Timeline</h3>
      
      <div className="relative">
        <div className="absolute left-3 top-0 bottom-0 w-px bg-slate-200" />
        
        <div className="space-y-4">
          {history.slice(0, 10).map((entry, idx) => (
            <div key={idx} className="relative flex gap-3 pl-8">
              <div className="absolute left-0 w-6 h-6 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                <History className="w-3 h-3 text-slate-400" />
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">{entry.action}</p>
                  <span className="text-xs text-slate-400">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                  </span>
                </div>
                {entry.details && <p className="text-xs text-slate-500 mt-1">{entry.details}</p>}
                {entry.by && <p className="text-xs text-slate-400 mt-1">by {entry.by}</p>}
              </div>
            </div>
          ))}
          
          {history.length === 0 && (
            <div className="pl-8 text-sm text-slate-400">No activity recorded</div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// JOB INFORMATION PANEL (Overview Tab - Left Side)
// =============================================================================
function JobInformationPanel({ job, onEdit }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="job-information-panel">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Job Information</h3>
        <button
          onClick={onEdit}
          className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
        >
          <Edit className="w-3 h-3" />
          Edit
        </button>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Category & Sub Category */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-slate-500 block mb-1">Category</span>
            <span className="text-sm font-medium text-slate-800">{job.category || 'General'}</span>
          </div>
          <div>
            <span className="text-xs text-slate-500 block mb-1">Sub Category</span>
            <span className="text-sm font-medium text-slate-800">{job.sub_category || job.job_type || '-'}</span>
          </div>
        </div>
        
        {/* Reported By */}
        <div>
          <span className="text-xs text-slate-500 block mb-1">Reported By</span>
          <span className="text-sm font-medium text-slate-800">
            {job.resident_name || job.client_name || 'Unknown'} 
            {job.resident_type ? ` (${job.resident_type})` : ' (Resident)'}
          </span>
        </div>
        
        {/* Contact Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-slate-500 block mb-1">Contact</span>
            <span className="text-sm text-slate-800">{job.contact_phone || job.resident_phone || '-'}</span>
          </div>
          <div>
            <span className="text-xs text-slate-500 block mb-1">Email</span>
            <span className="text-sm text-slate-800 truncate block">{job.client_email || job.resident_email || '-'}</span>
          </div>
        </div>
        
        {/* Access Notes */}
        <div>
          <span className="text-xs text-slate-500 block mb-1">Access Notes</span>
          <span className="text-sm text-slate-800">{job.access_notes || '-'}</span>
        </div>
        
        {/* Problem Date & Emergency */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-slate-500 block mb-1">Problem Since</span>
            <span className="text-sm text-slate-800">
              {job.problem_date ? new Date(job.problem_date).toLocaleDateString() : 
               job.created_at ? new Date(job.created_at).toLocaleDateString() : '-'}
            </span>
          </div>
          <div>
            <span className="text-xs text-slate-500 block mb-1">Emergency</span>
            <span className={`text-sm font-medium ${job.priority === 'Emergency' ? 'text-red-600' : 'text-slate-800'}`}>
              {job.priority === 'Emergency' ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        
        {/* Tags */}
        {(job.tags?.length > 0 || job.category) && (
          <div>
            <span className="text-xs text-slate-500 block mb-2">Tags</span>
            <div className="flex flex-wrap gap-2">
              {(job.tags || [job.category, job.sub_category].filter(Boolean)).map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// LOCATION PANEL WITH MAP LINK
// =============================================================================
function LocationPanel({ job }) {
  const address = [
    job.park_name,
    job.plot_number ? `Plot ${job.plot_number}` : null,
    job.address?.street || job.park_address,
    job.address?.city || job.city,
    job.address?.postcode || job.postcode,
  ].filter(Boolean).join(', ');
  
  const mapQuery = encodeURIComponent(address);
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="location-panel">
      <h3 className="font-semibold text-slate-800 mb-3">Location</h3>
      
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">{job.park_name || 'Unknown Park'}</p>
          {job.plot_number && <p className="text-sm text-slate-600">Plot {job.plot_number}</p>}
          <p className="text-xs text-slate-500 mt-1">{job.park_address || job.address?.street || ''}</p>
          <p className="text-xs text-slate-500">{job.city || ''} {job.postcode || ''}</p>
          
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium"
          >
            <ExternalLink className="w-3 h-3" />
            View on Map
          </a>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ASSET PANEL WITH VIEW LINK
// =============================================================================
function AssetPanel({ job }) {
  const asset = job.asset || {};
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="asset-panel">
      <h3 className="font-semibold text-slate-800 mb-3">Asset ({job.assets?.length || 1})</h3>
      
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">
            {asset.name || job.category || 'Property'} - {job.plot_number ? `Plot ${job.plot_number}` : 'General'}
          </p>
          <p className="text-xs text-slate-500 mt-1">Asset Type: {asset.type || job.category || 'General'}</p>
          {asset.installed_date && (
            <p className="text-xs text-slate-500">Installed: {new Date(asset.installed_date).toLocaleDateString()}</p>
          )}
          
          <a
            href={`/phs/assets?search=${encodeURIComponent(job.park_name || '')}`}
            className="inline-flex items-center gap-1.5 mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium"
          >
            <Eye className="w-3 h-3" />
            View Asset
          </a>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// QUOTES PANEL - Commercial Control Centre
// =============================================================================
function QuotesPanel({ job, onRefresh, getAuthHeaders, onAction }) {
  const quotes = job.quotes || [];
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  
  // Benchmark data (would come from rate library in production)
  const benchmark = useMemo(() => {
    const category = job.category?.toLowerCase() || '';
    // Sample benchmarks by category - in production, fetch from rate library
    const benchmarks = {
      'plumbing': { low: 150, avg: 280, high: 450 },
      'electrical': { low: 200, avg: 350, high: 600 },
      'heating': { low: 250, avg: 400, high: 700 },
      'general': { low: 100, avg: 200, high: 400 },
      'emergency': { low: 200, avg: 450, high: 800 },
      'default': { low: 150, avg: 300, high: 500 },
    };
    return benchmarks[category] || benchmarks['default'];
  }, [job.category]);
  
  // Calculate variance for a quote
  const getQuoteAnalysis = (quote) => {
    const total = quote.total_inc_vat || quote.total || 0;
    const variance = total - benchmark.avg;
    const variancePercent = benchmark.avg > 0 ? ((variance / benchmark.avg) * 100).toFixed(1) : 0;
    
    let status = 'normal';
    if (total <= benchmark.low) status = 'low';
    else if (total >= benchmark.high) status = 'high';
    else if (total > benchmark.avg * 1.1) status = 'above_avg';
    else if (total < benchmark.avg * 0.9) status = 'below_avg';
    
    return { total, variance, variancePercent, status };
  };

  return (
    <div className="space-y-4" data-testid="quotes-panel">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800">Quotes ({quotes.length})</h3>
            <p className="text-xs text-slate-500">Commercial control centre</p>
          </div>
          <div className="flex gap-2">
            {quotes.length > 1 && (
              <button
                onClick={() => setShowCompare(!showCompare)}
                className="px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                Compare Quotes
              </button>
            )}
            <button
              onClick={() => onAction('create_quote')}
              className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Create Quote
            </button>
          </div>
        </div>
        
        {/* Benchmark Reference */}
        <div className="p-3 bg-slate-50 rounded-lg mb-4">
          <p className="text-xs font-medium text-slate-600 mb-2">
            Benchmark Reference ({job.category || 'General'})
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-xs text-slate-600">Low: £{benchmark.low}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-xs text-slate-600">Avg: £{benchmark.avg}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-slate-600">High: £{benchmark.high}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quote Cards */}
      {quotes.length > 0 ? (
        <div className="space-y-3">
          {quotes.map((quote, idx) => {
            const analysis = getQuoteAnalysis(quote);
            const isSelected = selectedQuote?.id === quote.id;
            
            return (
              <div 
                key={idx} 
                className={`bg-white rounded-xl border transition cursor-pointer ${
                  isSelected ? 'border-teal-400 ring-2 ring-teal-100' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setSelectedQuote(isSelected ? null : quote)}
              >
                <div className="p-4">
                  {/* Quote Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-800">
                        Quote #{quote.quote_number || idx + 1}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        quote.status === 'Approved' ? 'bg-green-100 text-green-700' :
                        quote.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {quote.status || 'Pending'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-800">
                        £{analysis.total.toLocaleString()}
                      </p>
                      <p className={`text-xs font-medium ${
                        analysis.status === 'high' ? 'text-red-600' :
                        analysis.status === 'above_avg' ? 'text-amber-600' :
                        analysis.status === 'below_avg' || analysis.status === 'low' ? 'text-green-600' :
                        'text-slate-500'
                      }`}>
                        {analysis.variance >= 0 ? '+' : ''}£{analysis.variance.toFixed(0)} ({analysis.variancePercent}% vs avg)
                      </p>
                    </div>
                  </div>
                  
                  {/* Benchmark Bar */}
                  <div className="mb-4">
                    <div className="h-2 bg-slate-100 rounded-full relative overflow-hidden">
                      <div 
                        className="absolute h-full bg-green-400 rounded-l-full"
                        style={{ width: `${(benchmark.low / benchmark.high) * 100}%` }}
                      />
                      <div 
                        className="absolute h-full bg-blue-400"
                        style={{ 
                          left: `${(benchmark.low / benchmark.high) * 100}%`,
                          width: `${((benchmark.avg - benchmark.low) / benchmark.high) * 100}%`
                        }}
                      />
                      <div 
                        className="absolute h-full bg-amber-400"
                        style={{ 
                          left: `${(benchmark.avg / benchmark.high) * 100}%`,
                          width: `${((benchmark.high - benchmark.avg) / benchmark.high) * 100}%`
                        }}
                      />
                      {/* Quote marker */}
                      <div 
                        className="absolute w-1 h-4 bg-slate-800 -top-1 rounded-full"
                        style={{ left: `${Math.min((analysis.total / benchmark.high) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Quote Breakdown */}
                  <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block">Labour</span>
                      <span className="font-medium text-slate-800">£{(quote.labour_cost || 0).toLocaleString()}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block">Materials</span>
                      <span className="font-medium text-slate-800">£{(quote.materials_cost || 0).toLocaleString()}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block">Net</span>
                      <span className="font-medium text-slate-800">£{(quote.total_exc_vat || quote.total_ex_vat || 0).toLocaleString()}</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block">VAT</span>
                      <span className="font-medium text-slate-800">£{(quote.vat_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  
                  {/* Quote metadata */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                    <span>Submitted: {quote.submitted_at ? new Date(quote.submitted_at).toLocaleDateString() : 'N/A'}</span>
                    <span>By: {quote.contractor_name || job.contractor_name || 'Contractor'}</span>
                    {quote.valid_until && <span>Valid until: {new Date(quote.valid_until).toLocaleDateString()}</span>}
                  </div>
                  
                  {/* Action Buttons */}
                  {quote.status === 'Pending' && (
                    <div className="flex gap-2 pt-3 border-t border-slate-100">
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction('approve'); }}
                        className="flex-1 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                      >
                        Approve Quote
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction('reject'); }}
                        className="flex-1 py-2 text-xs bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-medium"
                      >
                        Reject
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction('request_revision'); }}
                        className="px-4 py-2 text-xs bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 font-medium"
                      >
                        Request Revision
                      </button>
                    </div>
                  )}
                  
                  {quote.status === 'Approved' && !job.po_number && (
                    <div className="flex gap-2 pt-3 border-t border-slate-100">
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction('create_po'); }}
                        className="flex-1 py-2 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
                      >
                        Create PO
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction('create_follow_on'); }}
                        className="px-4 py-2 text-xs bg-white text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 font-medium"
                      >
                        Create Follow-On
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Expanded Details */}
                {isSelected && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50/50">
                    <div className="grid grid-cols-2 gap-4">
                      {/* CAPEX / Recharge Tags */}
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-2">Cost Tags</p>
                        <div className="flex gap-2">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={quote.is_capex || false}
                              onChange={() => {}}
                              className="w-3.5 h-3.5 rounded border-slate-300"
                            />
                            <span className="text-xs text-slate-700">CAPEX</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={quote.is_recharge || false}
                              onChange={() => {}}
                              className="w-3.5 h-3.5 rounded border-slate-300"
                            />
                            <span className="text-xs text-slate-700">Recharge Resident</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Approval Info */}
                      {quote.approved_by && (
                        <div>
                          <p className="text-xs font-medium text-slate-600 mb-2">Approval</p>
                          <p className="text-xs text-slate-700">
                            Approved by {quote.approved_by} on {quote.approved_at ? new Date(quote.approved_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Quote Notes */}
                    {quote.notes && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-600 mb-1">Notes</p>
                        <p className="text-xs text-slate-700 bg-white p-2 rounded border border-slate-200">{quote.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600 mb-1">No quotes received</p>
          <p className="text-xs text-slate-400 mb-4">Request a quote from the assigned contractor</p>
          <button
            onClick={() => onAction('chase')}
            className="px-4 py-2 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Chase for Quote
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FINANCE PANEL - Operational Finance Workflow
// =============================================================================
function FinancePanel({ job, onRefresh, getAuthHeaders, onAction }) {
  const invoice = job.invoice;
  const quote = job.quotes?.find(q => q.status === 'Approved') || job.quotes?.[0];
  
  // Evidence validation
  const evidence = job.evidence || { photos: [], documents: [] };
  const hasAfterPhotos = evidence.photos?.some(p => p.stage === 'after');
  const hasSATNote = evidence.documents?.some(d => d.type === 'satnote') || !!job.sat_note;
  const hasInvoice = evidence.documents?.some(d => d.type === 'invoice') || !!invoice;
  
  // Variance calculations
  const quoteValue = quote?.total_inc_vat || quote?.total || 0;
  const approvedValue = job.approved_value || quoteValue;
  const invoiceValue = invoice?.total || invoice?.total_inc_vat || 0;
  const variance = invoiceValue - approvedValue;
  const variancePercent = approvedValue > 0 ? ((variance / approvedValue) * 100).toFixed(1) : 0;
  const hasVarianceIssue = Math.abs(variance) > approvedValue * 0.05; // >5% variance
  
  // Workflow status
  const getWorkflowStage = () => {
    if (job.paid) return { stage: 'paid', label: 'Paid', color: 'green' };
    if (invoice?.status === 'Approved') return { stage: 'payment_pending', label: 'Payment Pending', color: 'blue' };
    if (invoice) return { stage: 'invoice_review', label: 'Invoice Review', color: 'amber' };
    if (job.status?.toLowerCase().includes('awaiting invoice')) return { stage: 'awaiting_invoice', label: 'Awaiting Invoice', color: 'slate' };
    return { stage: 'pre_invoice', label: 'Pre-Invoice', color: 'slate' };
  };
  
  const workflow = getWorkflowStage();
  
  // Validation checks for invoice approval
  const validationChecks = [
    { label: 'Quote Approved', pass: !!quote && quote.status === 'Approved', required: true },
    { label: 'PO Issued', pass: !!job.po_number, required: true },
    { label: 'After Photos', pass: hasAfterPhotos, required: true },
    { label: 'SAT Note', pass: hasSATNote, required: true },
    { label: 'Invoice Uploaded', pass: hasInvoice, required: true },
    { label: 'No Variance Issue', pass: !hasVarianceIssue, required: false },
  ];
  
  const canApproveInvoice = validationChecks.filter(c => c.required && !c.pass).length === 0;

  return (
    <div className="space-y-4" data-testid="finance-panel">
      {/* Workflow Stage */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Finance Workflow</h3>
          <span className={`px-3 py-1 text-xs rounded-full font-medium ${
            workflow.color === 'green' ? 'bg-green-100 text-green-700' :
            workflow.color === 'blue' ? 'bg-blue-100 text-blue-700' :
            workflow.color === 'amber' ? 'bg-amber-100 text-amber-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {workflow.label}
          </span>
        </div>
        
        {/* Finance Progress */}
        <div className="flex items-center justify-between mb-4">
          {['Awaiting Invoice', 'Invoice Review', 'Approved', 'Payment Pending', 'Paid'].map((step, idx) => {
            const stages = ['awaiting_invoice', 'invoice_review', 'approved', 'payment_pending', 'paid'];
            const currentIdx = stages.indexOf(workflow.stage);
            const stepIdx = idx;
            const isComplete = stepIdx < currentIdx || (workflow.stage === 'paid' && stepIdx <= currentIdx);
            const isCurrent = stepIdx === currentIdx;
            
            return (
              <div key={step} className="flex flex-col items-center flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mb-1 ${
                  isComplete ? 'bg-green-500 text-white' :
                  isCurrent ? 'bg-teal-500 text-white ring-2 ring-teal-200' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {isComplete ? <Check className="w-3 h-3" /> : idx + 1}
                </div>
                <span className="text-[10px] text-slate-500 text-center">{step}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Financial Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 mb-4">Financial Summary</h3>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <span className="text-xs text-slate-500 block mb-1">Quote Value</span>
            <span className="text-lg font-bold text-slate-800">
              £{quoteValue.toLocaleString()}
            </span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <span className="text-xs text-slate-500 block mb-1">Approved Value</span>
            <span className="text-lg font-bold text-slate-800">
              £{approvedValue.toLocaleString()}
            </span>
          </div>
          <div className={`p-3 rounded-lg ${hasVarianceIssue ? 'bg-red-50' : 'bg-slate-50'}`}>
            <span className="text-xs text-slate-500 block mb-1">Invoice Value</span>
            <span className={`text-lg font-bold ${hasVarianceIssue ? 'text-red-600' : 'text-slate-800'}`}>
              £{invoiceValue.toLocaleString()}
            </span>
            {hasVarianceIssue && (
              <span className="text-[10px] text-red-500 block">
                {variance > 0 ? '+' : ''}£{variance.toFixed(0)} ({variancePercent}%)
              </span>
            )}
          </div>
          <div className={`p-3 rounded-lg ${job.paid ? 'bg-green-50' : 'bg-slate-50'}`}>
            <span className="text-xs text-slate-500 block mb-1">Payment Status</span>
            <span className={`text-lg font-bold ${job.paid ? 'text-green-600' : 'text-slate-800'}`}>
              {job.paid ? 'Paid' : 'Unpaid'}
            </span>
          </div>
        </div>
        
        {/* Variance Warning */}
        {hasVarianceIssue && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-red-800">Invoice exceeds approved quote</p>
              <p className="text-[10px] text-red-600">
                Variance of £{Math.abs(variance).toFixed(0)} ({Math.abs(variancePercent)}%) requires review
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Validation Checklist */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Invoice Approval Checklist</h3>
        
        <div className="space-y-2">
          {validationChecks.map((check, idx) => (
            <div 
              key={idx}
              className={`flex items-center justify-between p-2 rounded-lg ${
                check.pass ? 'bg-green-50' : check.required ? 'bg-red-50' : 'bg-slate-50'
              }`}
            >
              <span className="text-xs text-slate-700">{check.label}</span>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                check.pass ? 'bg-green-500' : check.required ? 'bg-red-400' : 'bg-slate-300'
              }`}>
                {check.pass ? (
                  <Check className="w-3 h-3 text-white" />
                ) : (
                  <X className="w-3 h-3 text-white" />
                )}
              </div>
            </div>
          ))}
        </div>
        
        {!canApproveInvoice && invoice && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Cannot approve invoice until all required items are complete
            </p>
          </div>
        )}
      </div>
      
      {/* PO Status */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Purchase Order</h3>
        {job.po_number ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">PO #{job.po_number}</p>
                <p className="text-xs text-slate-500">
                  Issued {job.po_date ? new Date(job.po_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            <button className="text-xs text-teal-600 hover:text-teal-700">View PO</button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">No PO issued yet</p>
            {quote?.status === 'Approved' && (
              <button
                onClick={() => onAction?.('create_po')}
                className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Create PO
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Invoice Details */}
      {invoice && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Invoice</h3>
            <span className={`px-2 py-1 text-xs rounded-full ${
              invoice.status === 'Paid' ? 'bg-green-100 text-green-700' :
              invoice.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {invoice.status || 'Pending'}
            </span>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">Invoice #{invoice.invoice_number}</p>
              <p className="text-xs text-slate-500">
                Submitted {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <p className="text-lg font-bold text-slate-800">£{invoiceValue.toLocaleString()}</p>
          </div>
          
          {/* Invoice Actions */}
          {invoice.status !== 'Paid' && invoice.status !== 'Approved' && (
            <div className="flex gap-2">
              <button
                onClick={() => onAction?.('approve_invoice')}
                disabled={!canApproveInvoice}
                className={`flex-1 py-2 text-xs rounded-lg font-medium ${
                  canApproveInvoice 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                Approve Invoice
              </button>
              <button
                onClick={() => onAction?.('query_invoice')}
                className="flex-1 py-2 text-xs bg-white text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50 font-medium"
              >
                Raise Query
              </button>
            </div>
          )}
          
          {invoice.status === 'Approved' && !job.paid && (
            <button
              onClick={() => onAction?.('mark_paid')}
              className="w-full py-2 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
            >
              Mark as Paid
            </button>
          )}
        </div>
      )}
      
      {/* No Invoice */}
      {!invoice && workflow.stage === 'awaiting_invoice' && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Awaiting invoice from contractor</p>
          <button
            onClick={() => onAction?.('chase_invoice')}
            className="mt-3 px-4 py-2 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Chase for Invoice
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LINKED JOBS PANEL - Follow-On & Related Works
// =============================================================================
function LinkedJobsPanel({ job, onNavigate }) {
  const linkedJobs = [...(job.linked_jobs || []), ...(job.follow_on_jobs || [])];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="linked-jobs-panel">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">Linked Jobs ({linkedJobs.length})</h3>
      </div>
      
      {linkedJobs.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {linkedJobs.map((linkedJob, idx) => (
            <div 
              key={idx} 
              className="p-4 hover:bg-slate-50 cursor-pointer"
              onClick={() => onNavigate(linkedJob.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{linkedJob.job_reference || `Job ${idx + 1}`}</p>
                  <p className="text-xs text-slate-500">{linkedJob.description || linkedJob.category}</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">
                  {linkedJob.status || 'In Progress'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center">
          <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No linked jobs</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ASSETS PANEL - Asset & Compliance Context
// =============================================================================
function AssetsPanel({ job }) {
  const assets = job.assets || [{ name: job.category, type: job.category, park: job.park_name }];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="assets-panel">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">Assets ({assets.length})</h3>
      </div>
      
      <div className="divide-y divide-slate-100">
        {assets.map((asset, idx) => (
          <div key={idx} className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">{asset.name || 'Property Asset'}</p>
                <p className="text-xs text-slate-500">Type: {asset.type || 'General'}</p>
                <p className="text-xs text-slate-500">Location: {asset.park || job.park_name}</p>
                {asset.compliance_status && (
                  <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                    asset.compliance_status === 'Compliant' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {asset.compliance_status}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// AUDIT PANEL - Full Governance History
// =============================================================================
function AuditPanel({ job }) {
  const auditLog = job.audit_log || job.history || [];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="audit-panel">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">Audit Trail</h3>
        <p className="text-xs text-slate-500">{auditLog.length} events recorded</p>
      </div>
      
      {auditLog.length > 0 ? (
        <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
          {auditLog.map((entry, idx) => (
            <div key={idx} className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <History className="w-3 h-3 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800">{entry.action || entry.event}</p>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                    </span>
                  </div>
                  {entry.details && <p className="text-xs text-slate-500 mt-1">{entry.details}</p>}
                  {entry.by && <p className="text-xs text-slate-400 mt-1">by {entry.by}</p>}
                  {entry.old_value && entry.new_value && (
                    <p className="text-xs text-slate-400 mt-1">
                      Changed from "{entry.old_value}" to "{entry.new_value}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center">
          <History className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No audit events recorded</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAKE ACTION PANEL (Sticky Right)
// =============================================================================
function TakeActionPanel({ job, onAction, loading }) {
  const status = job.status?.toLowerCase()?.replace(/\s+/g, '_');
  
  // Context-aware actions based on status
  const getActions = () => {
    const universalActions = [
      { id: 'note', label: 'Add Internal Note', desc: 'Add note for team', icon: FileText, color: 'slate' },
      { id: 'escalate', label: 'Escalate SLA', desc: 'Flag urgent', icon: AlertTriangle, color: 'red' },
      { id: 'hold', label: 'Put On Hold', desc: 'Pause job', icon: Pause, color: 'slate' },
    ];
    
    const cancelAction = { id: 'cancel', label: 'Cancel Job', desc: 'Close job', icon: X, color: 'red' };
    
    const statusActions = {
      job_created: [
        { id: 'assign', label: 'Assign Contractor', desc: 'Select and assign', icon: UserPlus, color: 'teal', primary: true },
      ],
      unassigned: [
        { id: 'assign', label: 'Assign Contractor', desc: 'Select and assign', icon: UserPlus, color: 'teal', primary: true },
      ],
      assigned: [
        { id: 'chase', label: 'Chase Contractor', desc: 'Request quote', icon: MessageSquare, color: 'blue', primary: true },
        { id: 'reassign', label: 'Reassign', desc: 'Change contractor', icon: UserPlus, color: 'purple' },
      ],
      awaiting_quote: [
        { id: 'chase', label: 'Chase for Quote', desc: 'Send reminder', icon: MessageSquare, color: 'orange', primary: true },
        { id: 'reassign', label: 'Reassign', desc: 'Change contractor', icon: UserPlus, color: 'purple' },
      ],
      quote_awaiting_review: [
        { id: 'review_quote', label: 'Review Quote', desc: 'View details', icon: Eye, color: 'yellow', primary: true },
        { id: 'approve', label: 'Approve Quote', desc: 'Accept and proceed', icon: CheckCircle, color: 'green' },
        { id: 'reject', label: 'Reject Quote', desc: 'Request revision', icon: X, color: 'red' },
      ],
      quote_awaiting_approval: [
        { id: 'approve', label: 'Approve Quote', desc: 'Accept and proceed', icon: CheckCircle, color: 'green', primary: true },
        { id: 'reject', label: 'Reject Quote', desc: 'Request revision', icon: X, color: 'red' },
      ],
      approved: [
        { id: 'issue_po', label: 'Generate PO', desc: 'Create purchase order', icon: Receipt, color: 'teal', primary: true },
        { id: 'schedule', label: 'Schedule Work', desc: 'Set dates', icon: Calendar, color: 'blue' },
      ],
      po_issued: [
        { id: 'schedule', label: 'Schedule Work', desc: 'Set work dates', icon: Calendar, color: 'blue', primary: true },
      ],
      scheduled: [
        { id: 'confirm_start', label: 'Confirm Started', desc: 'Work has begun', icon: Zap, color: 'blue', primary: true },
        { id: 'reschedule', label: 'Reschedule', desc: 'Change date', icon: Calendar, color: 'slate' },
      ],
      in_progress: [
        { id: 'mark_complete', label: 'Mark Complete', desc: 'Work finished', icon: CheckCircle, color: 'green', primary: true },
        { id: 'chase', label: 'Chase Update', desc: 'Request progress', icon: MessageSquare, color: 'blue' },
        { id: 'upload_evidence', label: 'Upload Evidence', desc: 'Add photos/docs', icon: Camera, color: 'purple' },
      ],
      completed: [
        { id: 'request_invoice', label: 'Request Invoice', desc: 'From contractor', icon: Receipt, color: 'orange', primary: true },
        { id: 'create_followon', label: 'Create Follow-On', desc: 'New job', icon: Plus, color: 'purple' },
      ],
      completed_awaiting_review: [
        { id: 'verify_complete', label: 'Verify Complete', desc: 'Confirm work done', icon: CheckCircle, color: 'green', primary: true },
        { id: 'request_rework', label: 'Request Rework', desc: 'Issue with work', icon: AlertTriangle, color: 'red' },
      ],
      awaiting_invoice: [
        { id: 'chase', label: 'Chase Invoice', desc: 'Send reminder', icon: MessageSquare, color: 'orange', primary: true },
      ],
      invoice_submitted: [
        { id: 'approve_invoice', label: 'Approve Invoice', desc: 'Process payment', icon: DollarSign, color: 'green', primary: true },
        { id: 'dispute_invoice', label: 'Dispute', desc: 'Query amount', icon: AlertTriangle, color: 'red' },
      ],
      invoiced: [
        { id: 'approve_invoice', label: 'Approve Invoice', desc: 'Process payment', icon: DollarSign, color: 'green', primary: true },
        { id: 'dispute_invoice', label: 'Dispute', desc: 'Query amount', icon: AlertTriangle, color: 'red' },
      ],
      invoice_queried: [
        { id: 'chase', label: 'Chase Response', desc: 'Follow up', icon: MessageSquare, color: 'orange', primary: true },
        { id: 'approve_invoice', label: 'Approve Invoice', desc: 'Accept as is', icon: CheckCircle, color: 'green' },
      ],
      on_hold: [
        { id: 'resume', label: 'Resume Job', desc: 'Continue work', icon: Play, color: 'green', primary: true },
      ],
      follow_on_required: [
        { id: 'create_followon', label: 'Create Follow-On', desc: 'New job', icon: Plus, color: 'teal', primary: true },
      ],
      sla_breach: [
        { id: 'escalate', label: 'Escalate', desc: 'Raise urgency', icon: AlertTriangle, color: 'red', primary: true },
        { id: 'reassign', label: 'Reassign', desc: 'Change contractor', icon: UserPlus, color: 'purple' },
      ],
    };
    
    const contextActions = statusActions[status] || [];
    const showCancel = !['completed', 'paid', 'cancelled', 'closed'].includes(status);
    
    return [...contextActions, ...universalActions, ...(showCancel ? [cancelAction] : [])];
  };
  
  const actions = getActions();
  
  const colorStyles = {
    teal: { bg: 'bg-teal-600 hover:bg-teal-700', text: 'text-white' },
    blue: { bg: 'bg-blue-600 hover:bg-blue-700', text: 'text-white' },
    green: { bg: 'bg-green-600 hover:bg-green-700', text: 'text-white' },
    orange: { bg: 'bg-orange-500 hover:bg-orange-600', text: 'text-white' },
    yellow: { bg: 'bg-yellow-500 hover:bg-yellow-600', text: 'text-white' },
    purple: { bg: 'bg-purple-600 hover:bg-purple-700', text: 'text-white' },
    red: { bg: 'bg-red-50 hover:bg-red-100 border border-red-200', text: 'text-red-700' },
    slate: { bg: 'bg-slate-100 hover:bg-slate-200', text: 'text-slate-700' },
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="take-action-panel">
      <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <h3 className="font-semibold text-slate-800">Take Action</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Current: {STATUS_CONFIG[job.status]?.label || job.status}
        </p>
      </div>
      
      <div className="p-3 space-y-2">
        {actions.map(action => {
          const Icon = action.icon;
          const style = colorStyles[action.color];
          
          return (
            <button
              key={action.id}
              onClick={() => onAction(action.id)}
              disabled={loading}
              data-testid={`action-${action.id}`}
              className={`w-full p-3 rounded-xl transition flex items-center gap-3 ${
                action.primary ? style.bg + ' ' + style.text : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
              } disabled:opacity-50`}
            >
              <Icon className={`w-4 h-4 ${action.primary ? '' : 'text-slate-500'}`} />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">{action.label}</p>
                <p className={`text-xs ${action.primary ? 'opacity-80' : 'text-slate-500'}`}>{action.desc}</p>
              </div>
            </button>
          );
        })}
        
        {/* Danger Zone */}
        <div className="pt-3 mt-3 border-t border-slate-100">
          <button
            onClick={() => onAction('cancel')}
            disabled={loading}
            className="w-full p-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 flex items-center gap-3"
          >
            <Ban className="w-4 h-4" />
            <div className="flex-1 text-left">
              <p className="text-sm font-medium">Cancel Job</p>
              <p className="text-xs text-red-500">Close or cancel this job</p>
            </div>
          </button>
          
          <button
            onClick={() => onAction('escalate')}
            disabled={loading}
            className="w-full mt-2 p-2 text-sm text-slate-500 hover:text-amber-600 flex items-center justify-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            Escalate Job
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// QUICK CONTACT PANEL
// =============================================================================
function QuickContactPanel({ job, onAction }) {
  const contacts = [
    {
      type: 'resident',
      name: job.resident_name || job.client_name || 'Resident',
      phone: job.resident_phone || job.client_phone,
      email: job.resident_email || job.client_email,
    },
    {
      type: 'contractor',
      name: job.contractor_name || 'Contractor',
      phone: job.contractor?.phone || job.contractor_phone,
      email: job.contractor?.email || job.contractor_email,
      company: job.contractor?.company_name,
    },
  ];
  
  const handleCall = (phone) => {
    if (phone) window.open(`tel:${phone}`, '_self');
    else toast.info('No phone number available');
  };
  
  const handleWhatsApp = (phone, name) => {
    if (!phone) return toast.info('No phone available');
    let normalized = phone.replace(/\D/g, '');
    if (normalized.startsWith('0')) normalized = '44' + normalized.slice(1);
    const msg = encodeURIComponent(`Hi ${name}, regarding job ${job.job_reference}`);
    window.open(`https://wa.me/${normalized}?text=${msg}`, '_blank');
  };
  
  const handleEmail = (email) => {
    if (email) window.open(`mailto:${email}?subject=Re: Job ${job.job_reference}`, '_blank');
    else toast.info('No email available');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="quick-contact-panel">
      <h3 className="font-semibold text-slate-800 mb-4">Quick Contact</h3>
      
      <div className="space-y-4">
        {contacts.map(contact => (
          <div key={contact.type}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">{contact.type}</span>
            </div>
            <p className="text-sm font-medium text-slate-800">{contact.name}</p>
            {contact.company && <p className="text-xs text-slate-500">{contact.company}</p>}
            <p className="text-xs text-slate-400 mb-2">{contact.phone || 'No phone'}</p>
            
            <div className="flex gap-2">
              <button 
                onClick={() => handleCall(contact.phone)}
                className="flex-1 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-1"
              >
                <Phone className="w-3 h-3" /> Call
              </button>
              <button 
                onClick={() => handleWhatsApp(contact.phone, contact.name)}
                className="flex-1 py-1.5 text-xs border border-green-200 text-green-600 rounded-lg hover:bg-green-50 flex items-center justify-center gap-1"
              >
                <MessageSquare className="w-3 h-3" /> WhatsApp
              </button>
              <button 
                onClick={() => handleEmail(contact.email)}
                className="flex-1 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-1"
              >
                <Mail className="w-3 h-3" /> Email
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// JOB TABS - Main Workspace Tabs (Overview, Comms, Timeline, Evidence, Quotes, Finance, Linked, Assets, Audit)
// =============================================================================
const JOB_TABS = [
  { key: 'overview', label: 'Overview', icon: FileText },
  { key: 'comms', label: 'Communications', icon: MessageSquare },
  { key: 'timeline', label: 'Timeline', icon: History },
  { key: 'evidence', label: 'Evidence', icon: Camera },
  { key: 'quotes', label: 'Quotes', icon: Receipt },
  { key: 'finance', label: 'Finance', icon: DollarSign },
  { key: 'linked', label: 'Linked Jobs', icon: Link2 },
  { key: 'assets', label: 'Assets', icon: Building2 },
  { key: 'audit', label: 'Audit', icon: FileCheck },
];

function JobTabs({ job, activeTab, onTabChange }) {
  // Calculate tab badges (counts/status)
  const getTabBadge = (tabKey) => {
    switch (tabKey) {
      case 'quotes':
        const pendingQuotes = (job.quotes || []).filter(q => q.status === 'Pending').length;
        return pendingQuotes > 0 ? { count: pendingQuotes, color: 'bg-amber-500' } : null;
      case 'comms':
        const unreadComms = (job.notes || []).filter(n => !n.read).length;
        return unreadComms > 0 ? { count: unreadComms, color: 'bg-blue-500' } : null;
      case 'evidence':
        const hasEvidence = (job.evidence?.photos?.length > 0 || job.evidence?.documents?.length > 0);
        const needsEvidence = ['In Progress', 'Completed'].includes(job.status) && !hasEvidence;
        return needsEvidence ? { text: '!', color: 'bg-red-500' } : null;
      case 'linked':
        const linkedCount = (job.linked_jobs || []).length + (job.follow_on_jobs || []).length;
        return linkedCount > 0 ? { count: linkedCount, color: 'bg-slate-500' } : null;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4" data-testid="job-tabs">
      <div className="flex items-center overflow-x-auto border-b border-slate-100">
        {JOB_TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const badge = getTabBadge(tab.key);
          
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              data-testid={`job-tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition relative ${
                isActive 
                  ? 'border-teal-600 text-teal-600 bg-teal-50/50' 
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {badge && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white ${badge.color}`}>
                  {badge.count || badge.text}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// EDIT RESIDENT MODAL
// =============================================================================
function EditResidentModal({ isOpen, onClose, job, onSave, getAuthHeaders }) {
  const [formData, setFormData] = useState({
    resident_name: '',
    resident_phone: '',
    resident_email: '',
    plot_number: '',
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (isOpen && job) {
      setFormData({
        resident_name: job.resident_name || job.client_name || '',
        resident_phone: job.resident_phone || job.contact_phone || '',
        resident_email: job.resident_email || job.client_email || '',
        plot_number: job.plot_number || '',
      });
    }
  }, [isOpen, job]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/wyldecrest/jobs/${job.id}`, formData, { 
        headers: getAuthHeaders(), withCredentials: true 
      });
      toast.success('Resident info updated');
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Resident Information">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Resident Name</label>
          <input
            type="text"
            value={formData.resident_name}
            onChange={(e) => handleChange('resident_name', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
            placeholder="Enter resident name"
            data-testid="resident-name-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.resident_phone}
              onChange={(e) => handleChange('resident_phone', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
              placeholder="07xxx xxxxxx"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.resident_email}
              onChange={(e) => handleChange('resident_email', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
              placeholder="resident@email.com"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Plot Number</label>
          <input
            type="text"
            value={formData.plot_number}
            onChange={(e) => handleChange('plot_number', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
            placeholder="e.g. Plot 42"
          />
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="save-resident-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// CREATE QUOTE MODAL - Internal Quote Creation
// =============================================================================
function CreateQuoteModal({ isOpen, onClose, job, onSave, getAuthHeaders }) {
  const [formData, setFormData] = useState({
    labour_cost: '',
    materials_cost: '',
    vat_rate: '20',
    description: '',
    is_capex: false,
    is_recharge: false,
    contractor_id: '',
    valid_days: '30',
  });
  const [saving, setSaving] = useState(false);
  const [contractors, setContractors] = useState([]);
  
  useEffect(() => {
    if (isOpen) {
      // Reset form
      setFormData({
        labour_cost: '',
        materials_cost: '',
        vat_rate: '20',
        description: job?.description || '',
        is_capex: false,
        is_recharge: false,
        contractor_id: job?.contractor_id || '',
        valid_days: '30',
      });
      // Fetch contractors
      axios.get(`${API}/api/contractors`, { headers: getAuthHeaders() })
        .then(res => setContractors(res.data || []))
        .catch(err => console.error('Failed to fetch contractors:', err));
    }
  }, [isOpen, job]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Calculate totals
  const labourCost = parseFloat(formData.labour_cost) || 0;
  const materialsCost = parseFloat(formData.materials_cost) || 0;
  const netTotal = labourCost + materialsCost;
  const vatRate = parseFloat(formData.vat_rate) || 0;
  const vatAmount = netTotal * (vatRate / 100);
  const grossTotal = netTotal + vatAmount;
  
  const handleSave = async () => {
    if (netTotal <= 0) {
      toast.error('Please enter quote values');
      return;
    }
    
    setSaving(true);
    try {
      const quoteData = {
        job_id: job.id,
        contractor_id: formData.contractor_id || job.contractor_id || null,
        labour_cost: labourCost,
        materials_cost: materialsCost,
        total_exc_vat: netTotal,
        vat_rate: vatRate,
        vat_amount: vatAmount,
        total_inc_vat: grossTotal,
        description: formData.description,
        is_capex: formData.is_capex,
        is_recharge: formData.is_recharge,
        valid_until: new Date(Date.now() + parseInt(formData.valid_days) * 24 * 60 * 60 * 1000).toISOString(),
        status: 'Pending',
        created_by: 'Helpdesk',
      };
      
      await axios.post(`${API}/api/fm/jobs/${job.id}/quotes`, quoteData, { 
        headers: getAuthHeaders(), withCredentials: true 
      });
      
      toast.success('Quote created successfully');
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Create quote error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create quote');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Quote">
      <div className="space-y-4">
        {/* Contractor Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Contractor</label>
          <select
            value={formData.contractor_id}
            onChange={(e) => handleChange('contractor_id', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
          >
            <option value="">Select contractor...</option>
            {contractors.map(c => (
              <option key={c.id} value={c.id}>{c.company_name || c.name} ({c.trade})</option>
            ))}
          </select>
        </div>
        
        {/* Cost Inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Labour Cost (£)</label>
            <input
              type="number"
              step="0.01"
              value={formData.labour_cost}
              onChange={(e) => handleChange('labour_cost', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Materials Cost (£)</label>
            <input
              type="number"
              step="0.01"
              value={formData.materials_cost}
              onChange={(e) => handleChange('materials_cost', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
              placeholder="0.00"
            />
          </div>
        </div>
        
        {/* VAT */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">VAT Rate (%)</label>
          <select
            value={formData.vat_rate}
            onChange={(e) => handleChange('vat_rate', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none"
          >
            <option value="0">0% (Zero Rated)</option>
            <option value="5">5% (Reduced)</option>
            <option value="20">20% (Standard)</option>
          </select>
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description / Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-teal-400 outline-none resize-none"
            placeholder="Quote details, scope of work..."
          />
        </div>
        
        {/* Quote Summary */}
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Net Total:</span>
            <span className="font-medium text-slate-800">£{netTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">VAT ({formData.vat_rate}%):</span>
            <span className="font-medium text-slate-800">£{vatAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm border-t border-slate-200 pt-2">
            <span className="text-slate-700 font-medium">Gross Total:</span>
            <span className="font-bold text-slate-900">£{grossTotal.toFixed(2)}</span>
          </div>
        </div>
        
        {/* Tags */}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_capex}
              onChange={(e) => handleChange('is_capex', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">CAPEX</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_recharge}
              onChange={(e) => handleChange('is_recharge', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">Recharge Resident</span>
          </label>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || netTotal <= 0}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="save-quote-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Quote
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// ASSIGN CONTRACTOR MODAL
// =============================================================================
function AssignContractorModal({ isOpen, onClose, job, onAssign, getAuthHeaders }) {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  useEffect(() => {
    if (isOpen) fetchContractors();
  }, [isOpen]);
  
  const fetchContractors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/contractors`, { headers: getAuthHeaders(), withCredentials: true });
      setContractors(res.data || []);
    } catch (err) {
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };
  
  const filteredContractors = contractors.filter(c => 
    (c.company_name || c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleAssign = async () => {
    if (!selectedContractor) return toast.error('Select a contractor');
    setAssigning(true);
    try {
      const params = new URLSearchParams({ contractor_id: selectedContractor.id, send_notification: 'true' });
      if (scheduledDate) params.append('scheduled_date', scheduledDate);
      
      await axios.post(`${API}/api/fm/jobs/${job.id}/assign-contractor?${params}`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success(`Assigned to ${selectedContractor.company_name || selectedContractor.name}`);
      onAssign();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Contractor" size="lg">
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search contractors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:border-teal-400 outline-none"
          />
        </div>
        
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            </div>
          ) : filteredContractors.map(c => (
            <div
              key={c.id}
              onClick={() => setSelectedContractor(c)}
              className={`p-3 border-b border-slate-100 cursor-pointer transition ${
                selectedContractor?.id === c.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : 'hover:bg-slate-50'
              }`}
            >
              <p className="font-medium text-slate-800">{c.company_name || c.name}</p>
              <p className="text-sm text-slate-500">{(c.trades || []).join(', ') || 'General'}</p>
            </div>
          ))}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Scheduled Date (Optional)</label>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
          />
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedContractor || assigning}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
            Assign Contractor
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// ADD NOTE MODAL
// =============================================================================
function AddNoteModal({ isOpen, onClose, job, onNoteAdded, getAuthHeaders }) {
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    if (!note.trim()) return toast.error('Enter a note');
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=${encodeURIComponent(note)}&note_type=${noteType}`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Note added');
      setNote('');
      onNoteAdded();
      onClose();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Note">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Note Type</label>
          <select
            value={noteType}
            onChange={(e) => setNoteType(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="internal">Internal Note</option>
            <option value="contractor">To Contractor</option>
            <option value="resident">To Resident</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
            placeholder="Enter your note..."
          />
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!note.trim() || saving}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Add Note
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// CONFIRM ACTION MODAL
// =============================================================================
function ConfirmActionModal({ isOpen, onClose, title, message, onConfirm, confirmText = 'Confirm', confirmColor = 'teal' }) {
  const [processing, setProcessing] = useState(false);
  
  const handleConfirm = async () => {
    setProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Error handled by onConfirm
    } finally {
      setProcessing(false);
    }
  };
  
  const colorClass = confirmColor === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700';
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <p className="text-slate-600">{message}</p>
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing}
            className={`px-4 py-2 text-sm text-white ${colorClass} rounded-lg disabled:opacity-50 flex items-center gap-2`}
          >
            {processing && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// SCHEDULE MODAL
// =============================================================================
function ScheduleModal({ isOpen, onClose, job, onScheduled, getAuthHeaders }) {
  const [scheduledDate, setScheduledDate] = useState(job?.scheduled_date || '');
  const [scheduledTime, setScheduledTime] = useState(job?.scheduled_time || '');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    if (!scheduledDate) return toast.error('Select a date');
    setSaving(true);
    try {
      const params = new URLSearchParams({ scheduled_date: scheduledDate });
      if (scheduledTime) params.append('scheduled_time', scheduledTime);
      
      await axios.post(`${API}/api/fm/jobs/${job.id}/schedule?${params}`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success('Job scheduled');
      onScheduled();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Schedule Job">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Scheduled Date</label>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Time (Optional)</label>
          <input
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
          />
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!scheduledDate || saving}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Schedule
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// EDIT JOB MODAL
// =============================================================================
function EditJobModal({ isOpen, onClose, job, onSave, getAuthHeaders }) {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (isOpen && job) {
      setFormData({
        description: job.description || '',
        priority: job.priority || 'Medium',
        category: job.category || job.job_type || '',
        site_name: job.site_name || job.park_name || '',
        postcode: job.postcode || '',
        plot_number: job.plot_number || '',
        resident_name: job.resident_name || job.client_name || '',
        resident_phone: job.resident_phone || job.contact_phone || '',
      });
    }
  }, [isOpen, job]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Try wyldecrest endpoint first (preferred), fallback to fm/jobs
      try {
        await axios.patch(`${API}/api/wyldecrest/jobs/${job.id}`, formData, { 
          headers: getAuthHeaders(), withCredentials: true 
        });
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 405) {
          // Fallback to fm_system endpoint
          await axios.patch(`${API}/api/fm_system/jobs/${job.id}`, formData, { 
            headers: getAuthHeaders(), withCredentials: true 
          });
        } else {
          throw err;
        }
      }
      toast.success('Job updated');
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const priorities = ['Emergency', 'High', 'Medium', 'Low'];
  const categories = ['PLUMBING', 'ELECTRICAL', 'ROOFING', 'GLAZING', 'GENERAL_BUILDING', 'BOILER_SERVICE', 'DRAIN_UNBLOCK'];
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Job">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Site/Park</label>
            <input
              type="text"
              value={formData.site_name || ''}
              onChange={(e) => handleChange('site_name', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="edit-site-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Postcode</label>
            <input
              type="text"
              value={formData.postcode || ''}
              onChange={(e) => handleChange('postcode', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select
              value={formData.priority || 'Medium'}
              onChange={(e) => handleChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              {priorities.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <select
              value={formData.category || ''}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">Select...</option>
              {categories.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
            </select>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
            data-testid="edit-description"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Resident Name</label>
            <input
              type="text"
              value={formData.resident_name || ''}
              onChange={(e) => handleChange('resident_name', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Resident Phone</label>
            <input
              type="text"
              value={formData.resident_phone || ''}
              onChange={(e) => handleChange('resident_phone', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="save-edit-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// MAIN WORKSPACE COMPONENT
// =============================================================================
export default function EnterpriseJobWorkspace({ jobId: propJobId, onBack, wyldecrestUser }) {
  const params = useParams();
  const navigate = useNavigate();
  const jobId = propJobId || params.jobId;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [communications, setCommunications] = useState([]);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showResidentModal, setShowResidentModal] = useState(false);
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState(false);
  
  // Job Workspace tabs state
  const [activeJobTab, setActiveJobTab] = useState('overview');
  
  // Inline drawer states (Phase 2A)
  const [showQuoteDrawer, setShowQuoteDrawer] = useState(false);
  const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}`, { 
        headers: getAuthHeaders(), withCredentials: true 
      });
      const jobData = res.data.job || res.data;
      setJob(jobData);
      
      // Extract communications
      const comms = [];
      if (jobData.notes) jobData.notes.forEach(n => comms.push({ ...n, type: n.type || 'internal' }));
      if (jobData.contractor_messages) jobData.contractor_messages.forEach(m => comms.push({ ...m, type: 'contractor' }));
      comms.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setCommunications(comms);
    } catch (err) {
      console.error('Error fetching job:', err);
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/phs/helpdesk');
  };

  // ==========================================================================
  // ACTION HANDLERS
  // ==========================================================================
  const handleAction = async (actionId) => {
    switch(actionId) {
      case 'edit':
        setShowEditModal(true);
        break;
      case 'edit_resident':
        setShowResidentModal(true);
        break;
      case 'create_quote':
        setShowCreateQuoteModal(true);
        break;
      case 'view_contractor':
        // Navigate to contractor workspace
        if (job.contractor_id) {
          window.open(`/phs/contractors?search=${encodeURIComponent(job.contractor_name || '')}`, '_blank');
        }
        break;
      case 'assign':
      case 'reassign':
        setShowAssignModal(true);
        break;
      case 'note':
        setShowNoteModal(true);
        break;
      case 'schedule':
      case 'reschedule':
        setShowScheduleModal(true);
        break;
      case 'chase':
        await handleChase();
        break;
      case 'approve':
        // Open quote review drawer instead of direct action
        if (job.quotes?.length > 0) {
          setSelectedQuote(job.quotes[0]);
          setShowQuoteDrawer(true);
        } else {
          await handleApproveQuote();
        }
        break;
      case 'reject':
        if (job.quotes?.length > 0) {
          setSelectedQuote(job.quotes[0]);
          setShowQuoteDrawer(true);
        } else {
          await handleRejectQuote();
        }
        break;
      case 'review_quote':
        if (job.quotes?.length > 0) {
          setSelectedQuote(job.quotes[0]);
          setShowQuoteDrawer(true);
        }
        break;
      case 'approve_invoice':
        if (job.invoice) {
          setSelectedInvoice(job.invoice);
          setShowInvoiceDrawer(true);
        }
        break;
      case 'dispute_invoice':
        if (job.invoice) {
          setSelectedInvoice(job.invoice);
          setShowInvoiceDrawer(true);
        }
        break;
      case 'issue_po':
        await handleIssuePO();
        break;
      case 'confirm_start':
        await handleStatusChange('In Progress');
        break;
      case 'mark_complete':
        setShowCompleteModal(true);
        break;
      case 'request_invoice':
        await handleRequestInvoice();
        break;
      case 'approve_invoice':
        await handleApproveInvoice();
        break;
      case 'cancel':
        setShowCancelModal(true);
        break;
      case 'escalate':
        await handleEscalate();
        break;
      case 'create_followon':
        await handleCreateFollowOn();
        break;
      case 'hold':
        await handlePutOnHold();
        break;
      case 'resume':
        await handleResume();
        break;
      case 'upload_evidence':
        // Scroll to evidence panel or open evidence upload modal
        document.getElementById('evidence-section')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'verify_complete':
        await handleStatusChange('Awaiting Invoice');
        break;
      case 'request_rework':
        await handleRequestRework();
        break;
      case 'review_quote':
        // Scroll to quotes section
        document.getElementById('quotes-section')?.scrollIntoView({ behavior: 'smooth' });
        break;
      default:
        console.warn(`Unhandled action: ${actionId}`);
    }
  };
  
  const handlePutOnHold = async () => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${API}/api/wyldecrest/jobs/${job.id}`,
        { status: 'On Hold' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=Job%20put%20on%20hold%20by%20helpdesk&note_type=internal`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Job put on hold');
      fetchJob();
    } catch (err) {
      toast.error('Failed to put job on hold');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleResume = async () => {
    setActionLoading(true);
    try {
      // Resume to the previous status (or Assigned as default)
      const resumeStatus = job.previous_status || 'Assigned';
      await axios.patch(
        `${API}/api/wyldecrest/jobs/${job.id}`,
        { status: resumeStatus },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=Job%20resumed%20by%20helpdesk&note_type=internal`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Job resumed');
      fetchJob();
    } catch (err) {
      toast.error('Failed to resume job');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRequestRework = async () => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${API}/api/wyldecrest/jobs/${job.id}`,
        { status: 'In Progress' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=Rework%20requested%20-%20work%20not%20satisfactory&note_type=contractor`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Rework requested');
      fetchJob();
    } catch (err) {
      toast.error('Failed to request rework');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${API}/api/wyldecrest/jobs/${job.id}`,
        { status: newStatus },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleChase = async () => {
    setActionLoading(true);
    try {
      const chaseNote = `Chase sent to ${job.contractor_name || 'contractor'}`;
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=${encodeURIComponent(chaseNote)}&note_type=internal`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      await axios.post(`${API}/api/fm/jobs/${job.id}/resend-notification`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Chase sent');
      fetchJob();
    } catch (err) {
      toast.error('Failed to send chase');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleApproveQuote = async () => {
    const pendingQuotes = (job?.quotes || []).filter(q => q.status === 'Pending');
    if (!pendingQuotes.length) return toast.error('No pending quotes');
    
    setActionLoading(true);
    try {
      const quoteId = pendingQuotes[0].id || pendingQuotes[0].quote_id;
      await axios.post(`${API}/api/fm/quotes/${quoteId}/approve`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Quote approved');
      fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRejectQuote = async () => {
    const pendingQuotes = (job?.quotes || []).filter(q => q.status === 'Pending');
    if (!pendingQuotes.length) return toast.error('No pending quotes');
    
    setActionLoading(true);
    try {
      const quoteId = pendingQuotes[0].id || pendingQuotes[0].quote_id;
      await axios.post(`${API}/api/fm/quotes/${quoteId}/reject`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Quote rejected');
      fetchJob();
    } catch (err) {
      toast.error('Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleIssuePO = async () => {
    const pendingQuotes = (job?.quotes || []).filter(q => q.status === 'Pending');
    if (pendingQuotes.length > 0) {
      await handleApproveQuote();
    } else {
      await handleStatusChange('PO Issued');
    }
  };
  
  const handleRequestInvoice = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=Invoice%20requested&note_type=contractor`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      await axios.post(`${API}/api/fm/jobs/${job.id}/resend-notification`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Invoice request sent');
      fetchJob();
    } catch (err) {
      toast.error('Failed to request invoice');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleApproveInvoice = async () => {
    await handleStatusChange('Paid');
  };
  
  const handleEscalate = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/fm/jobs/${job.id}/add-note?note=Job%20escalated%20by%20helpdesk&note_type=internal`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Job escalated');
      fetchJob();
    } catch (err) {
      toast.error('Failed to escalate');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCreateFollowOn = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${job.id}/create-follow-on`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      const followOnId = res.data?.job_id || res.data?.id;
      toast.success('Follow-on job created');
      if (followOnId) navigate(`/phs/helpdesk/job/${followOnId}`);
    } catch (err) {
      toast.error('Failed to create follow-on');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCancelJob = async () => {
    try {
      await axios.patch(`${API}/api/fm/jobs/${job.id}/status?status=Closed`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Job cancelled');
      fetchJob();
    } catch (err) {
      toast.error('Failed to cancel');
      throw err;
    }
  };
  
  const handleCompleteJob = async () => {
    try {
      await axios.patch(`${API}/api/fm/jobs/${job.id}/status?status=Completed`, {}, 
        { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Job marked complete');
      fetchJob();
    } catch (err) {
      toast.error('Failed to complete');
      throw err;
    }
  };
  
  const handleAddNote = async (noteData) => {
    try {
      await axios.post(
        `${API}/api/fm/jobs/${jobId}/add-note?note=${encodeURIComponent(noteData.message)}&note_type=${noteData.type}`,
        {}, { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Note added');
      fetchJob();
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading job...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
        <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
        <p className="text-slate-500 mb-4">Job not found</p>
        <button onClick={handleBack} className="px-4 py-2 bg-teal-600 text-white rounded-lg">
          Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 lg:pb-4" data-testid="enterprise-job-workspace">
      {/* Sticky Header */}
      <JobHeader 
        job={job} 
        onBack={handleBack} 
        onAction={handleAction}
        loading={actionLoading}
        showMoreMenu={showMoreMenu}
        setShowMoreMenu={setShowMoreMenu}
      />
      
      {/* SLA Status Strip - Instant operational awareness */}
      <div className="px-4 lg:px-6 pt-4">
        <SLAStatusStrip job={job} />
      </div>
      
      {/* Job Tabs - Overview, Comms, Timeline, Evidence, Quotes, Finance, Linked, Assets, Audit */}
      <div className="px-4 lg:px-6 pt-4">
        <JobTabs job={job} activeTab={activeJobTab} onTabChange={setActiveJobTab} />
      </div>
      
      {/* Two/Three Column Layout Based on Active Tab */}
      <div className="px-4 lg:px-6 py-4">
        <div className="flex gap-4">
          {/* LEFT COLUMN - Tab Content */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* OVERVIEW TAB */}
            {activeJobTab === 'overview' && (
              <>
                {/* Workflow Progress */}
                <WorkflowProgressTracker job={job} />
                
                {/* Job Information Panel */}
                <JobInformationPanel job={job} onEdit={() => setShowEditModal(true)} />
                
                {/* Location Panel */}
                <LocationPanel job={job} />
                
                {/* Asset Panel */}
                <AssetPanel job={job} />
              </>
            )}
            
            {/* COMMUNICATIONS TAB */}
            {activeJobTab === 'comms' && (
              <CommunicationsPanel 
                job={job} 
                communications={communications}
                onAddNote={handleAddNote}
              />
            )}
            
            {/* TIMELINE TAB */}
            {activeJobTab === 'timeline' && (
              <TimelinePanel job={job} />
            )}
            
            {/* EVIDENCE TAB */}
            {activeJobTab === 'evidence' && (
              <EnhancedEvidencePanel 
                job={job}
                onRefresh={fetchJob}
                getAuthHeaders={getAuthHeaders}
              />
            )}
            
            {/* QUOTES TAB */}
            {activeJobTab === 'quotes' && (
              <QuotesPanel 
                job={job}
                onRefresh={fetchJob}
                getAuthHeaders={getAuthHeaders}
                onAction={handleAction}
              />
            )}
            
            {/* FINANCE TAB */}
            {activeJobTab === 'finance' && (
              <FinancePanel 
                job={job}
                onRefresh={fetchJob}
                getAuthHeaders={getAuthHeaders}
              />
            )}
            
            {/* LINKED JOBS TAB */}
            {activeJobTab === 'linked' && (
              <LinkedJobsPanel job={job} onNavigate={(id) => navigate(`/phs/helpdesk/job/${id}`)} />
            )}
            
            {/* ASSETS TAB */}
            {activeJobTab === 'assets' && (
              <AssetsPanel job={job} />
            )}
            
            {/* AUDIT TAB */}
            {activeJobTab === 'audit' && (
              <AuditPanel job={job} />
            )}
          </div>
          
          {/* RIGHT COLUMN - Sticky Action Panel */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-[200px] space-y-4">
              {/* Take Action */}
              <TakeActionPanel 
                job={job} 
                onAction={handleAction}
                loading={actionLoading}
              />
              
              {/* Job Progress (Compact) */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-3">Job Progress</h3>
                <WorkflowProgressTracker job={job} compact />
              </div>
              
              {/* SLA Summary */}
              <SLASummaryDonut job={job} />
              
              {/* Financial Snapshot */}
              <FinancialSnapshot job={job} />
              
              {/* Quick Contact */}
              <QuickContactPanel job={job} onAction={handleAction} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Action Bar - Higher z-index to be above platform badges */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 z-[9999]" style={{ paddingBottom: 'calc(3.5rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowNoteModal(true)}
            className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Note
          </button>
          <button 
            onClick={() => setActiveJobTab('evidence')}
            className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
          >
            <Camera className="w-3.5 h-3.5" />
            Evidence
          </button>
          <button 
            onClick={() => handleAction(
              job.contractor_name ? 'chase' : 'assign'
            )}
            className="flex-1 py-2.5 bg-teal-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1"
          >
            {job.contractor_name ? (
              <>
                <Phone className="w-3.5 h-3.5" />
                Chase
              </>
            ) : (
              <>
                <UserPlus className="w-3.5 h-3.5" />
                Assign
              </>
            )}
          </button>
          <button 
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className="py-2.5 px-3 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium flex items-center justify-center"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
        
        {/* More Actions Menu */}
        {showMoreMenu && (
          <div className="mt-2 p-2 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { handleAction('complete'); setShowMoreMenu(false); }}
                className="py-2 px-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-medium"
              >
                Complete Job
              </button>
              <button
                onClick={() => { handleAction('on_hold'); setShowMoreMenu(false); }}
                className="py-2 px-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-medium"
              >
                Put On Hold
              </button>
              <button
                onClick={() => { setActiveJobTab('quotes'); setShowMoreMenu(false); }}
                className="py-2 px-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-medium"
              >
                View Quotes
              </button>
              <button
                onClick={() => { handleAction('cancel'); setShowMoreMenu(false); }}
                className="py-2 px-3 bg-white border border-red-200 text-red-600 rounded-lg text-xs font-medium"
              >
                Cancel Job
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <AssignContractorModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        job={job}
        onAssign={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        job={job}
        onNoteAdded={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        job={job}
        onScheduled={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <EditJobModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        job={job}
        onSave={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <EditResidentModal
        isOpen={showResidentModal}
        onClose={() => setShowResidentModal(false)}
        job={job}
        onSave={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <CreateQuoteModal
        isOpen={showCreateQuoteModal}
        onClose={() => setShowCreateQuoteModal(false)}
        job={job}
        onSave={fetchJob}
        getAuthHeaders={getAuthHeaders}
      />
      
      <ConfirmActionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Job"
        message="Are you sure you want to cancel this job? This cannot be undone."
        onConfirm={handleCancelJob}
        confirmText="Cancel Job"
        confirmColor="red"
      />
      
      <ConfirmActionModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title="Mark Complete"
        message="Confirm this job is complete?"
        onConfirm={handleCompleteJob}
        confirmText="Mark Complete"
        confirmColor="teal"
      />
      
      {/* Quote Review Drawer (Phase 2A) */}
      <QuoteReviewDrawer
        isOpen={showQuoteDrawer}
        onClose={() => {
          setShowQuoteDrawer(false);
          setSelectedQuote(null);
        }}
        job={job}
        quote={selectedQuote}
        onApprove={handleApproveQuote}
        onReject={handleRejectQuote}
        onRequestRevision={async (note) => {
          // Request revision logic
          await axios.post(
            `${API}/api/fm/jobs/${job.id}/request-quote-revision`,
            { note },
            { headers: getAuthHeaders(), withCredentials: true }
          );
          fetchJob();
        }}
        getAuthHeaders={getAuthHeaders}
      />
      
      {/* Invoice Review Drawer (Phase 2A) */}
      <InvoiceReviewDrawer
        isOpen={showInvoiceDrawer}
        onClose={() => {
          setShowInvoiceDrawer(false);
          setSelectedInvoice(null);
        }}
        job={job}
        invoice={selectedInvoice}
        onApprove={async () => {
          await axios.post(
            `${API}/api/fm/jobs/${job.id}/approve-invoice`,
            {},
            { headers: getAuthHeaders(), withCredentials: true }
          );
          toast.success('Invoice approved');
          fetchJob();
        }}
        onQuery={async (note) => {
          await axios.post(
            `${API}/api/fm/jobs/${job.id}/query-invoice`,
            { note },
            { headers: getAuthHeaders(), withCredentials: true }
          );
          toast.success('Query sent to contractor');
          fetchJob();
        }}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}
